import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { api } from '@/lib/api/client';
import { useStoredState } from '@/lib/hooks';

export interface Property {
  id: string;
  name: string;
  code?: string;
  addressLine1?: string;
  city?: string;
  state?: string;
  pincode?: string;
  totalAreaSqM?: number;
  rules?: string[];
  amenities?: string[];
}

export const ALL_PROPERTIES = 'all';

interface PropertyContextValue {
  properties: readonly Property[];
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
  /** 'all' or a property id. */
  selectedId: string;
  /** The selected property, or null when "All properties" is chosen. */
  selected: Property | null;
  /** Pass straight into API queries: undefined means "every property". */
  propertyId: string | undefined;
  setSelectedId: (id: string) => void;
}

const PropertyContext = createContext<PropertyContextValue | null>(null);

export const propertiesQueryKey = ['properties'] as const;

/** Loads the owner's properties once and remembers which one is selected across the app. */
export function PropertyProvider({ children }: { children: ReactNode }) {
  const query = useQuery({ queryKey: propertiesQueryKey, queryFn: ({ signal }) => api.get<Property[]>('/properties', { signal }) });
  const [storedId, setSelectedId] = useStoredState<string>('movo.property', ALL_PROPERTIES);

  const value = useMemo<PropertyContextValue>(() => {
    const properties = query.data ?? [];
    // A stored id that no longer exists falls back to "all" instead of showing nothing.
    const selected = properties.find((property) => property.id === storedId) ?? null;
    return {
      properties,
      isLoading: query.isPending,
      error: query.error,
      refetch: () => void query.refetch(),
      selectedId: selected?.id ?? ALL_PROPERTIES,
      selected,
      propertyId: selected?.id,
      setSelectedId,
    };
  }, [query, storedId, setSelectedId]);

  return <PropertyContext.Provider value={value}>{children}</PropertyContext.Provider>;
}

export function useProperty(): PropertyContextValue {
  const context = useContext(PropertyContext);
  if (!context) {
    throw new Error('useProperty must be used inside the owner layout');
  }
  return context;
}
